// Konfiguracja axios dla wewnetrznego asystenta AI (WiseAi).
// Uzywa naszego backendu Symfony (/api/ui/assistant-ai/*) z autoryzacja Bearer
// pobierana z Redux store. NIE uzywa interceptorow normalizujacych z axios.ts —
// odpowiedzi przychodza w surowym ksztalcie DTO (CommonUiApiResponseDto).
// Ma wlasny response interceptor 401 -> refresh -> retry (asystent czesto wywoluje
// tool calls trwajace dlugo, w trakcie ktorych token JWT moze wygasnac).

import axios, { AxiosError } from 'axios';
import { store } from 'store';
import i18n from 'i18n';

import { refreshTokensAndRetry } from './refreshTokens';

interface RetryableConfig {
  __isRetryRequest?: boolean;
}

const { REACT_APP_API_BASE_URL, REACT_APP_API_BASE_PATH } = process.env;

const WISE_AI_BASE_URL = `${REACT_APP_API_BASE_URL ?? ''}${REACT_APP_API_BASE_PATH ?? '/api/ui'}/assistant-ai`;

export const wiseAiAxios = axios.create({
  baseURL: WISE_AI_BASE_URL
});

wiseAiAxios.interceptors.request.use((config) => {
  const authToken = store.getState().auth.token;
  const headers: { [key: string]: string } = {
    'Content-Language': i18n.language,
    Accept: 'application/json',
    // Explicit UTF-8 — bez tego niektore proxy/serwery interpretuja body
    // jako latin-1, co psuje polskie znaki (np. "narzędzi" → "narz?dzi").
    'Content-Type': 'application/json; charset=utf-8'
  };
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  config.headers = { ...config.headers, ...headers };
  return config;
});

// 401 -> refresh tokena -> ponowny request. Guard `__isRetryRequest` chroni przed petla
// gdy nowy token tez zwroci 401 (np. uzytkownik wylogowany / refresh token wygasl).
wiseAiAxios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as (typeof error.config & RetryableConfig) | undefined;
    const status = error.response?.status;
    const isAuthenticated = store.getState().auth.isAuthenticated;

    if (status === 401 && isAuthenticated && config && !config.__isRetryRequest) {
      config.__isRetryRequest = true;
      try {
        return await refreshTokensAndRetry(wiseAiAxios, error);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export { WISE_AI_BASE_URL };
